<template>
    <v-group @click="(event) => $emit('click', event)">
        <v-rect :config="configCellBigCardContainer" />

        <v-text v-if="isDecision" :config="configCellDecisionTitleText" />
        <v-text v-else-if="!isSequenceView" :config="configCellBigCardTitleText" />
        <v-text v-if="!isSequenceView && !isWhen && !isDecision" :config="actionSequencePlaceHolder" />

        <KonvaCampaignImage
            v-if="isDecision"
            :config="{ x: $options.BIG_CARD_PADDING, y: (elementHeight - $options.ICON_SIZE) / 3 , width: $options.ICON_SIZE, height: $options.ICON_SIZE }"
            :src="$options.V_DIAMOND_BIG_CARD_SRC"
        />

        <KonvaCampaignImage
            :config="ellipsisIconConfig"
            :src="$options.V_ELLIPSIS_SRC"
            @mouseenter="$emit('set-tooltip')"
        />

        <v-text
            v-if="isDecision"
            :config="decisionLabelConfig"
        />

        <v-group v-else :config="cardContainerOffsetConfig">
            <v-group
                v-for="(cardCell, index) in bigCardPreviewCells"
                :key="cardCell.id"
                :config="configBigCardCellGroup(index)"
            >
                <KonvaCampaignImage
                    :src="getBigCardImageUrl(cardCell)"
                    :config="{ y: isSequenceView ? $options.ICON_SIZE : 0, width: $options.ICON_SIZE, height: $options.ICON_SIZE }"
                />

                <v-text :config="getElementLabelConfig(cardCell)" />

                <v-text v-if="getElementSubLabelText(cardCell)" :config="getElementSubLabelConfig(cardCell)" />
            </v-group>
        </v-group>

        <v-group
            v-if="additionalElementCount"
            :config="{
                x: $options.BIG_CARD_PADDING * 2,
                y: elementHeight - $options.BIG_CARD_ADDITIONAL_OPTIONS_HEIGHT,
            }"
        >
            <v-text
                :config="configCellBigShowMore"
            />
            <KonvaCampaignImage
                :src="$options.V_ARROW_RIGHT_SRC"
                :config="{ x: 80, y: -1 }"
            />
        </v-group>

        <v-group
            v-if="lockIconConfig"
            :x="lockIconConfig.x"
            :y="lockIconConfig.y"
            :listening="true"
            @mouseenter="$emit('set-lock-tooltip')"
            @mouseleave="$emit('set-hide-lock-tooltip')"
        >
            <KonvaCampaignImage
                :config="{ ...lockIconConfig, x: 0, y: 0 }"
                :src="lockIconSrc"
            />
        </v-group>
    </v-group>
</template>

<script>
import {
    getIconPathByCellType,
    getBigCardElementHeight,
    getCellTypeLabelKey,
    getCellTypesHiddenFromBigCard,
    hasCustomName,
} from '../utils/cell/automation-display-utils';
import {
    getDecisionConfiguration,
} from '../decisionDiamonds/decision.api';
import {
    GRID_SIZE,
    FLOW_TYPE,
    GOALS,
    BIG_CARD_WIDTH,
    SEQUENCE_START_WIDTH,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
    BIG_CARD_ELEMENT_LIMIT,
    DECISION_TYPE,
    SEQUENCE_START,
    BIG_CARD_ELEMENT_OFFSET,
    BIG_CARD_PADDING,
    PROCESS_APPLY_REMOVE_TAG,
    BIG_CARD_ADDITIONAL_OPTIONS_HEIGHT,
} from '../utils/editor.constants';

import KonvaCampaignImage from './KonvaCampaignImage.vue';

const ICON_SIZE = 18;
const TEXT_MAX_WIDTH = BIG_CARD_WIDTH - ICON_SIZE - BIG_CARD_PADDING * 4;
const PADDING_OFFSET = 8;

export default {
    V_ELLIPSIS_SRC: '/resources/funnel/images/svg/v-ellipsis.svg',
    V_ARROW_RIGHT_SRC: '/resources/svg/arrow-long-right-blue.svg',
    V_DIAMOND_BIG_CARD_SRC: '/resources/funnel/images/svg/decision_big_card.svg',
    ICON_SIZE,
    BIG_CARD_PADDING,
    BIG_CARD_ADDITIONAL_OPTIONS_HEIGHT,
    TEXT_MAX_WIDTH,
    components: {
        KonvaCampaignImage,
    },
    props: {
        enabledIntegrations: {
            type: Array,
            required: true,
        },
        isSequenceView: {
            type: Boolean,
            default: false,
        },
        cell: {
            type: Object,
            required: true,
        },
        bigCardCells: {
            type: Array,
            default: () => [],
        },
        borderColor: {
            type: String,
            required: true,
        },
        lockIconConfig: {
            type: Object,
            default: null,
        },
        lockIconSrc: {
            type: String,
            default: '',
        },
    },
    emits: ['click', 'set-tooltip'],
    data() {
        return {
            numberOfRules: 0,
        };
    },

    computed: {
        isWhen() {
            return [...Object.values(GOALS), INTEGRATION_TRIGGER_TYPE].includes(this.cell.type);
        },

        isDecision() {
            return this.cell.type === DECISION_TYPE;
        },

        isSequenceStart() {
            return this.cell.type === SEQUENCE_START;
        },

        elementHeight() {
            return getBigCardElementHeight(this.bigCardCells, this.isSequenceView, this.isWhen);
        },

        bigCardDisplayCells() {
            const hiddenCellTypes = getCellTypesHiddenFromBigCard(this.isSequenceView);

            return this.bigCardCells.filter((cell) => {
                return !(hiddenCellTypes.includes(cell.type));
            });
        },

        ellipsisIconConfig() {
            return {
                x: this.isSequenceStart ? BIG_CARD_WIDTH / 2 : BIG_CARD_WIDTH - (ICON_SIZE + BIG_CARD_PADDING),
                y: this.isSequenceView
                    ? BIG_CARD_PADDING + 5
                    : this.isDecision ? BIG_CARD_PADDING + PADDING_OFFSET
                        : BIG_CARD_PADDING,
                width: ICON_SIZE,
                height: ICON_SIZE,
            };
        },

        decisionLabelConfig() {
            const ruleCount = Number(this.numberOfRules);

            const ruleText = ruleCount
                ? this.$t('funnel.bigCardView.setup.rules', { rules: ruleCount })
                : this.$t('funnel.bigCardView.setup.required');

            return {
                x: BIG_CARD_PADDING * 2 + ICON_SIZE,
                y: BIG_CARD_PADDING * 3,
                text: ruleText,
                wrap: 'none',
                ellipsis: true,
                width: TEXT_MAX_WIDTH,
                fontSize: 10,
                fill: '#333',
                fontFamily: 'Proxima Nova',
            };
        },

        cardContainerOffsetConfig() {
            if (this.isSequenceView) return {};

            return {
                y: this.isWhen || this.isDecision
                    ? BIG_CARD_ELEMENT_OFFSET - PADDING_OFFSET
                    : BIG_CARD_ELEMENT_OFFSET + BIG_CARD_PADDING,
            };
        },

        configCellDecisionTitleText() {
            return {
                text: this.$t('funnel.bigCardView.dd'),
                x: (BIG_CARD_PADDING * 2) + ICON_SIZE,
                y: (this.elementHeight - ICON_SIZE) / 3,
                fill: '#000',
                align: 'left',
                fontSize: 14,
                fontFamily: 'Proxima Nova',
            };
        },

        configCellBigCardTitleText() {
            return {
                text: this.isWhen ? this.$t('funnel.bigCardView.when') : this.$t('funnel.bigCardView.then'),
                x: BIG_CARD_PADDING,
                y: BIG_CARD_PADDING,
                fill: '#000',
                align: 'left',
                fontSize: 18,
                fontFamily: 'Poppins Bold',
            };
        },

        actionSequencePlaceHolder() {
            return {
                text: this.$t('funnel.bigCardView.then.action.sequence'),
                x: BIG_CARD_PADDING,
                y: BIG_CARD_PADDING * 3,
                width: TEXT_MAX_WIDTH,
                fontSize: 12,
                fill: '#333',
                fontFamily: 'Proxima Nova',
                height: 50,
            };
        },

        configCellBigCardContainer() {
            return {
                fill: '#fff',
                shadowColor: 'black',
                stroke: this.borderColor,
                strokeWidth: 3,
                shadowOffset: { x: 0, y: 2 },
                shadowOpacity: 0.1,
                cornerRadius: 12,
                width: this.isSequenceStart
                    ? BIG_CARD_WIDTH - SEQUENCE_START_WIDTH
                    : BIG_CARD_WIDTH,
                height: this.elementHeight,
                shadowForStrokeEnabled: false,
                perfectDrawEnabled: false,
            };
        },

        configCellBigShowMore() {
            return {
                text: `${this.additionalElementCount} more actions`,
                fill: '#006ceb',
                align: 'left',
                fontSize: 10,
                fontFamily: 'Poppins Bold',
            };
        },

        bigCardPreviewCells() {
            return this.bigCardDisplayCells.slice(0, BIG_CARD_ELEMENT_LIMIT);
        },

        additionalElementCount() {
            return Math.max(this.bigCardDisplayCells.length - BIG_CARD_ELEMENT_LIMIT, 0);
        },
    },
    async mounted() {
        if (this.isDecision) {
            await this.configureDecisionMappings();
        }
    },

    methods: {
        async configureDecisionMappings() {
            const { value: { decisions } } = this.cell;

            const flowIds = [];
            const decisionIds = [];

            decisions.forEach(({ flowId, decisionId }) => {
                flowIds.push(flowId);
                decisionIds.push(decisionId);
            });

            const decisionConfiguration = await getDecisionConfiguration({
                flowIds,
                decisionIds,
                ...this.ddKeys,
            });

            const ruleGroupsTemp = decisionConfiguration.ruleGroups;
            const rulesMap = {};

            Object.values(ruleGroupsTemp).forEach((group) => {
                if (group || group.ruleGroupMap || Object.keys(group.ruleGroupMap).length > 0) {
                    rulesMap[group.id] = Object.keys(group.ruleGroupMap).length;
                }
            });

            this.numberOfRules = Object.values(rulesMap).reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        },

        getElementLabelConfig(cardCell) {
            const width = this.isSequenceView || this.isDecision
                ? TEXT_MAX_WIDTH - ICON_SIZE // room for ellipsis
                : TEXT_MAX_WIDTH;

            const cellTypeLabel = this.getCellTypeLabel(cardCell);

            return {
                text: cellTypeLabel,
                wrap: 'none',
                ellipsis: true,
                width,
                fontSize: 14,
                fontFamily: 'Proxima Nova',
                x: 32,
                y: this.isSequenceView
                    ? this.getElementSubLabelText(cardCell) ? 14 : 20
                    : this.getElementSubLabelText(cardCell) ? 0 : 3,
            };
        },

        getCellTypeLabel(cardCell) {
            switch (cardCell.type) {
            case INTEGRATION_TRIGGER_TYPE:
                return this.enabledIntegrations.find((integration) => {
                    return cardCell.value.integrationTriggerId === integration.id;
                })?.name;
            case INTEGRATION_ACTION_TYPE:
                return this.enabledIntegrations.find((integration) => {
                    return cardCell.value.integrationActionId === integration.id;
                })?.name;
            case FLOW_TYPE:
                return this.$t('funnel.edit.sideBar.template.flow');
            default:
                return this.$t(getCellTypeLabelKey(cardCell.type, this.isWhen));
            }
        },

        getElementSubLabelConfig(cardCell) {
            const width = this.isSequenceView || this.isDecision
                ? TEXT_MAX_WIDTH - ICON_SIZE // room for ellipsis
                : TEXT_MAX_WIDTH;

            return {
                text: this.getElementSubLabelText(cardCell),
                x: 32,
                y: this.isSequenceView ? 32 : ICON_SIZE,
                wrap: 'none',
                width,
                ellipsis: true,
                fontSize: 10,
                fill: '#333',
                fontFamily: 'Proxima Nova',
            };
        },

        getElementSubLabelText(cardCell) {
            const cellName = cardCell.value.name?.replaceAll('\n', ' ');
            const cellTypeLabel = this.getCellTypeLabel(cardCell);

            if (cardCell.type === PROCESS_APPLY_REMOVE_TAG && !hasCustomName(cardCell, cellTypeLabel)) {
                return this.applyOrRemoveTagLabel(cardCell);
            }

            return cellName === cellTypeLabel ? '' : cellName;
        },

        applyOrRemoveTagLabel(cardCell) {
            const { isApply, tagIds } = cardCell.value;

            if (!isApply) {
                return this.$t('funnel.edit.tag.new');
            }

            const applyOrRemove = (isApply === 'true') ? 'funnel.edit.tag.apply' : 'funnel.edit.tag.remove';
            const numTags = tagIds ? tagIds.length : 0;

            return this.$tc(applyOrRemove, numTags, { numTags });
        },

        getBigCardImageUrl(cell) {
            let iconColor;

            if (cell.type === INTEGRATION_TRIGGER_TYPE) {
                return this.enabledIntegrations.find((integration) => {
                    return cell.value.integrationTriggerId === integration.id;
                })?.iconUrl;
            }

            if (cell.type === INTEGRATION_ACTION_TYPE) {
                return this.enabledIntegrations.find((integration) => {
                    return cell.value.integrationActionId === integration.id;
                })?.iconUrl;
            }

            if (cell?.value.broken === '1') iconColor = 'red';
            else if (cell.type === FLOW_TYPE || cell.meta.parent !== '1') iconColor = 'blue';
            else iconColor = 'green';

            return getIconPathByCellType(cell.type, iconColor);
        },

        configBigCardCellGroup(index) {
            return {
                x: BIG_CARD_PADDING,
                y: index * BIG_CARD_ELEMENT_OFFSET,
                height: GRID_SIZE,
            };
        },
    },
};

</script>
